import { useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import { RequestFailed } from "src/components/RequestFailed";
import { format } from "date-fns";
import { Button, Icon } from "@chakra-ui/react";
import { Helmet } from "react-helmet";
import { FaArrowRight, FaRegMoneyBillAlt, FaArrowLeft } from "react-icons/fa";
import { GrLocation } from "react-icons/gr";
import {
  formatAsCurrency,
  formatContractJobAdDuration,
  formatEmploymentType,
} from "src/utlis/helpers";
import { RiBuildingLine } from "react-icons/ri";
import { MdOutlineBusinessCenter } from "react-icons/md";
import { Tags } from "lucide-react";
import { IoCheckmarkCircle } from "react-icons/io5";
import { RxCounterClockwiseClock } from "react-icons/rx";
import { logEvent } from "src/utlis/analytics";
import { GET_COMMUNITY_JOB_OPPORTUNITY } from "src/features/communities/graphql";
import { Loading } from "@/components/Loading";
import { useEffect } from "react";

export default function CommunityJobOpportunityDetails() {
  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      navigate("/opportunities/communities");
    }
  }, [id, navigate]);

  const {
    loading: jobLoading,
    error: jobError,
    data: jobData,
  } = useQuery(GET_COMMUNITY_JOB_OPPORTUNITY, {
    fetchPolicy: "network-only",
    variables: {
      id: id!,
    },
    skip: !id,
  });

  const jobOpportunity = jobData?.getCommunityJobOpportunityForPro;

  if (jobError) {
    return (
      <>
        <Helmet>
          <title>Job Opportunity Not Found - LaborHack</title>
        </Helmet>
        <RequestFailed text={jobError.message} />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{jobOpportunity?.title ? `${jobOpportunity.title} - LaborHack Community Opportunities` : "Job Opportunity - LaborHack"}</title>
      </Helmet>
      <div>
        <main>
          {jobLoading ? (
            <div className="flex justify-center items-center h-screen">
              <Loading />
            </div>
          ) : jobOpportunity ? (
            <section className="px-3 lg:px-[75px] w-full">
              <div className="w-full border-[#ACACAC]">
                <div className="w-full">
                  <div className="mb-6 lg:mb-8">
                    <Button
                      leftIcon={<Icon as={FaArrowLeft} w={4} h={4} />}
                      variant="link"
                      colorScheme="prussianBlue"
                      fontSize={{
                        base: "14px",
                        lg: "16px",
                      }}
                      onClick={() => navigate("/opportunities/communities")}
                    >
                      Back to community opportunities
                    </Button>
                  </div>
                  <div className="border-[#ACACAC] border-b-[1px] pb-6 lg:pb-8 flex flex-col lg:flex-row gap-4 lg:justify-between lg:items-end">
                    <div className="w-full flex flex-col gap-4 lg:gap-6">
                      <div className="w-[85%] lg:w-[55%]">
                        <h3 className="text-[#0E212D] text-[18px] lg:text-[23px] font-medium">
                          {jobOpportunity.title}
                        </h3>
                      </div>
                      <div className="flex flex-col gap-2">
                        <p className="text-[#6E6E6E] text-[14px] lg:text-[18px] font-normal">
                          Date posted:{" "}
                          <span className="text-[#000000]">
                            {format(new Date(jobOpportunity.createdAt), "PPP")}
                          </span>
                        </p>
                      </div>
                      {jobOpportunity.applicationDeadline && (
                        <div className="flex flex-col gap-2">
                          <p className="text-[#6E6E6E] text-[14px] lg:text-[18px] font-normal">
                            Application deadline:{" "}
                            <span className="text-[#883227]">
                              {format(
                                new Date(jobOpportunity.applicationDeadline),
                                "PPP"
                              )}
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col lg:flex-row gap-3">
                      <Button
                        bgColor={
                          jobOpportunity.hasApplied && !jobOpportunity.canApply
                            ? "#FBE2DE"
                            : "#ED6E5C"
                        }
                        color={
                          jobOpportunity.hasApplied && !jobOpportunity.canApply
                            ? "#F08677"
                            : "#FEFEFE"
                        }
                        h={{
                          base: "40px",
                          lg: "57px",
                        }}
                        fontSize={{
                          base: "16px",
                          lg: "20px",
                        }}
                        rightIcon={
                          <Icon
                            as={
                              jobOpportunity.hasApplied &&
                              !jobOpportunity.canApply
                                ? IoCheckmarkCircle
                                : FaArrowRight
                            }
                            fill={
                              jobOpportunity.hasApplied &&
                              !jobOpportunity.canApply
                                ? "#F08677"
                                : "#FEFEFE"
                            }
                            w={{
                              base: 4,
                              lg: 5,
                            }}
                            h={{
                              base: 4,
                              lg: 5,
                            }}
                            ml={0}
                            fontWeight={"500"}
                          />
                        }
                        isDisabled={
                          jobOpportunity.hasApplied && !jobOpportunity.canApply
                        }
                        onClick={() => {
                          logEvent("community_job_application_started", {
                            job_id: jobOpportunity.id,
                            job_title: jobOpportunity.title,
                          });

                          navigate(
                            `/opportunities/communities/${jobOpportunity.id}/apply`
                          );
                        }}
                      >
                        {jobOpportunity.hasApplied && !jobOpportunity.canApply
                          ? "Applied"
                          : "Apply"}
                      </Button>
                    </div>
                  </div>
                  <div className="border-[#ACACAC] border-b-[1px] py-6 lg:py-8">
                    <div
                      className="markdown"
                      dangerouslySetInnerHTML={{
                        __html: jobOpportunity.description,
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-[3fr,5fr] gap-6 lg:gap-8 py-6 lg:py-8">
                    <div className="flex items-center gap-4">
                      <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                        <Icon
                          as={GrLocation}
                          w={{
                            base: "20px",
                            lg: "20px",
                          }}
                          h={{
                            base: "20px",
                            lg: "20px",
                          }}
                          color={"#212121"}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                          Location
                        </p>
                        <p className="text-[#212121] text-[16px] lg:text-[20px]">
                          {`${jobOpportunity.location.area}, ${jobOpportunity.location.state}, ${jobOpportunity.location.country}`}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                        <Icon
                          as={MdOutlineBusinessCenter}
                          w={{
                            base: "20px",
                            lg: "20px",
                          }}
                          h={{
                            base: "20px",
                            lg: "20px",
                          }}
                          color={"#212121"}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                          Employment type
                        </p>
                        <p className="text-[#212121] text-[16px] lg:text-[20px]">
                          {`${formatEmploymentType(
                            jobOpportunity.employmentType
                          )}`}
                        </p>
                      </div>
                    </div>

                    {jobOpportunity.duration && (
                      <div className="flex items-center gap-4">
                        <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                          <Icon
                            as={RxCounterClockwiseClock}
                            w={{
                              base: "20px",
                              lg: "20px",
                            }}
                            h={{
                              base: "20px",
                              lg: "20px",
                            }}
                            color={"#212121"}
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                            Duration
                          </p>
                          <p className="text-[#212121] text-[16px] lg:text-[20px]">
                            {formatContractJobAdDuration(jobOpportunity.duration)}
                          </p>
                        </div>
                      </div>
                    )}

                    {jobOpportunity.salary && (
                      <div className="flex items-center gap-4">
                        <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                          <Icon
                            as={FaRegMoneyBillAlt}
                            w={{
                              base: "20px",
                              lg: "20px",
                            }}
                            h={{
                              base: "20px",
                              lg: "20px",
                            }}
                            color={"#212121"}
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                            {`Salary ${jobOpportunity.salary.cap ? "range" : ""}`}
                          </p>
                          <p className="text-[#212121] text-[16px] lg:text-[20px]">
                            {formatAsCurrency(
                              jobOpportunity.salary.starting,
                              jobOpportunity.salary.currencyCode
                            )}{" "}
                            {jobOpportunity.salary.cap
                              ? `- ${formatAsCurrency(
                                  jobOpportunity.salary.cap,
                                  jobOpportunity.salary.currencyCode
                                )}`
                              : ""}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-4">
                      <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                        <Icon
                          as={RiBuildingLine}
                          w={{
                            base: "20px",
                            lg: "20px",
                          }}
                          h={{
                            base: "20px",
                            lg: "20px",
                          }}
                          color={"#212121"}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                          Organization
                        </p>
                        <p className="text-[#212121] text-[16px] lg:text-[20px]">
                          {jobOpportunity.organization.name}
                        </p>
                      </div>
                    </div>

                    {jobOpportunity.tags && jobOpportunity.tags.length > 0 && (
                      <div className="flex items-start gap-4">
                        <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                          <Icon
                            as={Tags}
                            w={{
                              base: "20px",
                              lg: "20px",
                            }}
                            h={{
                              base: "20px",
                              lg: "20px",
                            }}
                            color={"#212121"}
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                            {jobOpportunity.tags.length > 1 ? "Tags" : "Tag"}
                          </p>
                          <p className="text-[#212121] text-[16px] lg:text-[20px]">
                            {jobOpportunity.tags
                              .map((tag) => tag.name)
                              .join(", ")}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          ) : (
            !jobOpportunity &&
            !jobLoading && (
              <div className="flex items-center justify-center h-full p-4 lg:p-8 border-[1px] border-[#ACACAC]">
                <p className="text-[#0E212D] text-lg lg:text-xl">
                  Job opportunity not found
                </p>
              </div>
            )
          )}
        </main>
      </div>
    </>
  );
}